import { withCompInfo } from '@wix/editor-elements-integrations'
import type { LinkBarCSSVars, LinkBarDefinition, LinkBarProps } from './LinkBar'
import { getImageProps } from '../../core/carmi/Image/Image'

const calculateMobileDimensions = (
	numOfItems: number,
	iconSize: number,
	spacing: number,
	isHorizontal: boolean,
	totalItemSize: number
) => {
	if (numOfItems === 0) {
		return { height: `${iconSize}px`, width: '5px' }
	}

	const itemSize = iconSize + spacing
	const actualTotalSize = totalItemSize > 300 ? 300 : totalItemSize
	const width = isHorizontal ? actualTotalSize : iconSize
	const height = isHorizontal
		? Math.ceil(numOfItems / Math.floor((width + spacing) / itemSize)) * iconSize
		: totalItemSize
	return { width: `${width}px`, height: `${height}px` }
}

const calculateDesktopDimensions = (
	numOfItems: number,
	iconSize: number,
	isHorizontal: boolean,
	totalItemSize: number
) => {
	if (numOfItems === 0) {
		return { height: `${iconSize}px`, width: `${iconSize}px` }
	}

	return {
		width: `${isHorizontal ? totalItemSize : iconSize}px`,
		height: `${isHorizontal ? iconSize : totalItemSize}px`,
	}
}

const calculateResponsiveDimensions = (numOfItems: number, isHorizontal: boolean, totalItemSize: number) => {
	return numOfItems > 0 && !isHorizontal ? { height: `${totalItemSize}px` } : {}
}

export const css = withCompInfo<LinkBarCSSVars, LinkBarDefinition>()(
	['hasResponsiveLayout', 'compProps', 'compData', 'isMobileView'],
	({ hasResponsiveLayout, compProps, compData, isMobileView }) => {
		const numOfItems = compData.items?.length ?? 0
		const totalItemSize = numOfItems * (compProps.iconSize + compProps.spacing) - compProps.spacing
		const isHorizontal = compProps.orientation === 'HORIZ'
		const containerSize = hasResponsiveLayout
			? calculateResponsiveDimensions(numOfItems, isHorizontal, totalItemSize)
			: isMobileView
				? calculateMobileDimensions(numOfItems, compProps.iconSize, compProps.spacing, isHorizontal, totalItemSize)
				: calculateDesktopDimensions(numOfItems, compProps.iconSize, isHorizontal, totalItemSize)

		return {
			'--item-size': `${compProps.iconSize}px`,
			'--item-margin': isHorizontal ? `0 ${compProps.spacing}px 0 0` : `0 0 ${compProps.spacing}px 0`,
			'--item-display': isHorizontal ? 'inline-block' : 'block',
			...(hasResponsiveLayout && { '--flex-direction': isHorizontal ? 'row' : 'column' }),
			...containerSize,
		} as LinkBarCSSVars
	}
)

export const props = withCompInfo<LinkBarProps, LinkBarDefinition>()(
	['hasResponsiveLayout', 'compProps', 'compData', 'compId', 'experiments'],
	({ hasResponsiveLayout, compProps, compData, compId, experiments }) => {
		const iconSize = compProps.iconSize
		const shouldSkipResponsiveFixed = hasResponsiveLayout || iconSize === undefined
		const dimensions = shouldSkipResponsiveFixed ? {} : { width: iconSize, height: iconSize }
		const images = (compData.items || []).map((image) =>
			getImageProps(
				{
					...image,
					alt: image.alt || image.title, // Setting 'alt' with fallback to 'title'
				},
				{
					containerId: `${image.id}-${compId}`, // Using template literals
					dimensions,
				},
				experiments
			)
		)

		return {
			images,
		}
	}
)
